import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import { IntlKey } from 'locales';
import { ProductHelpCenterAction } from 'models/analytics';
import { BaseSyntheticEvent, ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import styled from 'styled-components';
import { optionalMap } from 'utils/optional';

import { type ProductInfo, useHelpCenterProducts } from '../../../hooks/use-help-center-products';

interface LearnMoreProps {
  mini?: boolean;
  productType?: string;
  productSubType?: string;
}

const DONT_SHOW_AGAIN_KEY = 'learnMore.dontShowAgain';
export function LearnMore({ mini = false, productType, productSubType }: LearnMoreProps) {
  const { getProduct, openLearnMore, openProductCatalogue } = useHelpCenterProducts();

  const onClickLearnMore = (action: ProductHelpCenterAction) => {
    logAnalytics.trackEvent(
      mini ? 'Primary market product selector static' : 'Primary market static',
      action,
      `${productType}/${productSubType}`,
    );
    openLearnMore(productType, productSubType, action === 'Download factsheet');
  };

  const onClickAllProducts = () => {
    logAnalytics.trackEvent('Primary market static', 'Open tutorial');
    openProductCatalogue();
  };

  const [isOpen, setOpen] = useState(() => !localStorage.getItem(DONT_SHOW_AGAIN_KEY));

  const productInfo = getProduct(productType, productSubType);

  if (productInfo === undefined) {
    return null;
  }

  if (mini) {
    return (
      <LearnMoreCard role="button" onClick={() => onClickLearnMore('Open tutorial')}>
        <MiniLearnMore
          productType={productType}
          productSubType={productSubType}
          onClickLearnMore={onClickLearnMore}
          {...productInfo}
        />
      </LearnMoreCard>
    );
  }
  return (
    <LearnMoreCard role="button" onClick={() => onClickLearnMore('Open tutorial')} $isOpen={isOpen}>
      {!isOpen ? (
        <CollapsedLearnMore
          productType={productType}
          productSubType={productSubType}
          onClickLearnMore={onClickLearnMore}
          onShow={() => setOpen(true)}
          {...productInfo}
        />
      ) : (
        <ExpandedLearnMore
          productType={productType}
          productSubType={productSubType}
          onClickLearnMore={onClickLearnMore}
          onClickAllProducts={onClickAllProducts}
          onHide={() => setOpen(false)}
          {...productInfo}
        />
      )}
    </LearnMoreCard>
  );
}

interface MiniLearnMoreProps extends ProductInfo, Omit<LearnMoreProps, 'mini'> {
  onClickLearnMore(action: ProductHelpCenterAction): void;
}

function MiniLearnMore({ hasVideo, hasFactSheet, productType, productSubType, onClickLearnMore }: MiniLearnMoreProps) {
  const toLabel = optionalMap(useToProductTypeLabel());
  return (
    <div className="d-flex align-items-center p-12px gap-12px" role="button">
      <InfoIcon />
      <span className="me-auto">
        <h1 className="fs-5">{toLabel(productType)}</h1>
        <ProductSubType $mini>{toLabel(productSubType)}</ProductSubType>
      </span>
      {hasFactSheet && (
        <LMButton
          labelId="learnMore.factsheet"
          icon={<FactSheetIcon />}
          iconOnly
          variant="flat-primary"
          onClick={() => onClickLearnMore('Download factsheet')}
        />
      )}
      {hasVideo && (
        <LMButton
          labelId="learnMore.video"
          icon="play_circle_filled"
          iconOnly
          variant="flat-primary"
          onClick={() => onClickLearnMore('Start video')}
        />
      )}
    </div>
  );
}

interface CollapsedLearnMoreProps extends Omit<LearnMoreProps, 'mini'> {
  onClickLearnMore(action: ProductHelpCenterAction): void;
  onShow(): void;
}

function CollapsedLearnMore({ productType, productSubType, onClickLearnMore, onShow }: CollapsedLearnMoreProps) {
  const toLabel = optionalMap(useToProductTypeLabel());
  return (
    <div className="d-flex p-12px gap-12px align-items-center">
      <InfoIcon />
      <span className="flex-grow-1">
        <FormattedMessage
          id="learnMore.youveChosen"
          values={{ product: `${toLabel(productType)} / ${toLabel(productSubType)}` }}
        />
      </span>
      <LMButton
        labelId="learnMore.learnMore"
        variant="flat-secondary"
        onClick={() => onClickLearnMore('Open tutorial')}
      />
      <LMButton labelId="show" variant="flat-secondary" icon="keyboard_arrow_down" iconOnly onClick={onShow} />
    </div>
  );
}

interface ExpandedLearnMoreProps extends MiniLearnMoreProps {
  onClickAllProducts(): void;
  onHide(): void;
}

function ExpandedLearnMore({
  hasFactSheet,
  hasVideo,
  productType,
  productSubType,
  onHide,
  onClickLearnMore,
  onClickAllProducts,
}: ExpandedLearnMoreProps) {
  const toLabel = optionalMap(useToProductTypeLabel());
  const [dontShowAgain, setDontShowAgain] = useState(isDefined(localStorage.getItem(DONT_SHOW_AGAIN_KEY)));
  return (
    <div className="card-img-overlay d-flex flex-column p-0">
      <div className="card-header d-flex p-0">
        <div className="d-flex align-items-center flex-grow-1 ps-2">
          <InfoIcon />
        </div>
        <div className="d-block">
          <LMButton
            labelId={dontShowAgain ? 'learnMore.showAgain' : 'learnMore.dontShowAgain'}
            variant="link"
            onClick={() => {
              if (dontShowAgain) {
                localStorage.removeItem(DONT_SHOW_AGAIN_KEY);
              } else {
                onHide();
                localStorage.setItem(DONT_SHOW_AGAIN_KEY, 'true');
              }
              setDontShowAgain(was => !was);
            }}
          />
          <LMButton labelId="hide" icon="keyboard_arrow_up" iconOnly variant="flat-secondary" onClick={onHide} />
        </div>
      </div>
      <div className="card-body d-flex flex-column justify-content-end gap-1 px-12px pb-12px">
        <h1 className="h6">{toLabel(productType)}</h1>
        <ProductSubType>{toLabel(productSubType)}</ProductSubType>
      </div>
      <div className={classNames('card-footer d-flex px-12px pb-12px flex-row justify-content-end')}>
        <LMButton labelId="learnMore.discoverMore" variant="flat-secondary" onClick={onClickAllProducts} />
        {hasFactSheet && (
          <LMButton
            labelId="learnMore.factsheet"
            icon={<FactSheetIcon />}
            variant="flat-primary"
            onClick={() => onClickLearnMore('Download factsheet')}
          />
        )}
        {hasVideo && (
          <LMButton
            labelId="learnMore.video"
            icon="play_circle_filled"
            variant="flat-primary"
            onClick={() => onClickLearnMore('Start video')}
          />
        )}
      </div>
    </div>
  );
}

function FactSheetIcon() {
  return (
    <i className="icon icon-md">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        enable-background="new 0 0 24 24"
        height="24px"
        viewBox="0 0 24 24"
        width="24px"
        fill="var(--bs-primary)"
      >
        <g>
          <rect fill="none" height="24" width="24" />
          <path d="M14.17,5L19,9.83V19H5V5L14.17,5L14.17,5 M14.17,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V9.83 c0-0.53-0.21-1.04-0.59-1.41l-4.83-4.83C15.21,3.21,14.7,3,14.17,3L14.17,3z M7,15h10v2H7V15z M7,11h10v2H7V11z M7,7h7v2H7V7z" />
        </g>
      </svg>
    </i>
  );
}

const ProductSubType = styled.span.attrs<{ $mini?: boolean }>(({ $mini }) => ({
  className: $mini ? 'fs-6' : 'fw-medium',
}))`
  ${({ $mini }) => ($mini ? 'line-height: 1.2' : undefined)}
`;

const LearnMoreCard = styled.div.attrs<{ $isOpen?: boolean }>({
  className: 'card rounded-1 p-0 overflow-hidden clickable',
})`
  --bg-color: rgb(245, 243, 243);

  .dark-theme & {
    --bg-color: rgb(69, 64, 63);
  }

  height: ${({ $isOpen }) => ($isOpen ? '140px' : 'auto')};
  min-height: ${({ $isOpen }) => ($isOpen ? '140px' : '60px')};
  max-height: ${({ $isOpen }) => ($isOpen ? '140px' : '80px')};
  transition:
    height 0.2s ease-in-out,
    min-height 0.2s ease-in-out,
    max-height 0.2s ease-in-out;

  &,
  & .card-footer {
    background-color: var(--bg-color);
  }
`;

function InfoIcon() {
  return <i className="icon text-info clickable">help</i>;
}

function dontPropagate<E extends BaseSyntheticEvent>(callback: () => void) {
  return function unpropagatedCallback(event: E) {
    event.stopPropagation();
    callback();
  };
}

interface LMButtonProps {
  labelId: IntlKey;
  icon?: string | ReactNode;
  iconOnly?: true;
  variant: 'flat-primary' | 'flat-secondary' | 'link';

  onClick(): void;
}

function LMButton({ labelId, icon, iconOnly, variant, onClick }: LMButtonProps) {
  const { formatMessage } = useIntl();
  const label = formatMessage({ id: labelId });
  return (
    <button
      type="button"
      className={classNames(
        'text-nowrap btn',
        `btn-${variant}`,
        isDefined(icon) ? `btn-icon${iconOnly ? '' : '-end'}` : '',
      )}
      title={iconOnly ? label : undefined}
      aria-label={iconOnly ? label : undefined}
      onClick={dontPropagate(onClick)}
    >
      {iconOnly ? null : label}
      {typeof icon === 'string' ? <i className="icon">{icon}</i> : icon}
    </button>
  );
}
